import { createTheme, alpha } from '@mui/material/styles';
import { MODE_VARS } from './oceanTokens';

/**
 * Builds the MUI theme for a colour mode.
 *
 * `primary.main` is deliberately identical in both modes: a dozen places
 * paint it as a background under white text, so lightening it for dark mode
 * would have quietly broken their contrast. The lift happens in
 * `primary.light` and in the Ocean gradient stops instead.
 *
 * Both modes' CSS variables are emitted here through CssBaseline, so the
 * Ocean tokens follow `<html data-theme>` with no React re-render.
 */
const round = (n) => Math.round(n * 1000) / 1000;

export const buildTheme = (mode = 'light') => {
  const dark = mode === 'dark';
  // Shadows have to work much harder on a dark ground to read at all.
  const sh = (o) => `rgba(0,0,0,${Math.min(0.6, round(o * (dark ? 2.6 : 1)))})`;
  const subtleText = dark ? '#9CB4C0' : '#637381';
  const accent = dark ? '#38BDF8' : '#0891B2';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#0891B2',
        light: '#38BDF8',
        dark: dark ? '#0E7490' : '#0C4A6E',
        contrastText: '#fff',
      },
      secondary: {
        main: dark ? '#60A5FA' : '#1E3A8A',
        light: dark ? '#93C5FD' : '#60A5FA',
        dark: '#0F2560',
        contrastText: dark ? '#0A1319' : '#fff',
      },
      success: {
        main: dark ? '#4ADE80' : '#2e7d32',
        light: dark ? '#86EFAC' : '#4caf50',
      },
      warning: {
        main: dark ? '#FBBF24' : '#ed6c02',
        light: dark ? '#FCD34D' : '#ff9800',
      },
      error: {
        main: dark ? '#F87171' : '#d32f2f',
        light: dark ? '#FCA5A5' : '#ef5350',
      },
      background: {
        default: dark ? '#0A1319' : '#F1F5F8',
        paper: dark ? '#111E26' : '#ffffff',
      },
      text: {
        primary: dark ? '#E6EFF3' : '#08202E',
        secondary: dark ? '#9CB4C0' : '#576E7C',
      },
      divider: dark ? 'rgba(255,255,255,0.1)' : 'rgba(8,32,46,0.09)',
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.2,
      },
      h2: {
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.3,
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.4,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.1rem',
        lineHeight: 1.5,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500,
        color: subtleText,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: subtleText,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      `0 1px 3px ${sh(0.04)}, 0 1px 2px ${sh(0.06)}`,
      `0 2px 8px ${sh(0.06)}, 0 1px 4px ${sh(0.04)}`,
      `0 4px 12px ${sh(0.08)}`,
      `0 6px 16px ${sh(0.08)}, 0 2px 6px ${sh(0.04)}`,
      `0 8px 24px ${sh(0.1)}`,
      `0 12px 32px ${sh(0.1)}, 0 4px 12px ${sh(0.06)}`,
      `0 16px 40px ${sh(0.12)}`,
      `0 20px 48px ${sh(0.12)}, 0 8px 16px ${sh(0.06)}`,
      ...Array(16).fill(`0 20px 48px ${sh(0.12)}, 0 8px 16px ${sh(0.06)}`),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Both modes ship every time, so switching is one attribute on <html>
          // and every Ocean token repaints without React being involved.
          ':root': MODE_VARS.light,
          ':root[data-theme="dark"]': MODE_VARS.dark,
          '*': {
            margin: 0,
            padding: 0,
            boxSizing: 'border-box',
          },
          html: {
            scrollBehavior: 'smooth',
          },
          body: {
            backgroundColor: dark ? '#0A1319' : '#F1F5F8',
            transition: 'background-color .3s ease, color .3s ease',
          },
          '@media (prefers-reduced-motion: reduce)': {
            body: { transition: 'none' },
          },
          '::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '::-webkit-scrollbar-track': {
            background: dark ? '#0E1B23' : '#f1f1f1',
            borderRadius: '4px',
          },
          '::-webkit-scrollbar-thumb': {
            background: dark ? '#2C4250' : '#c1c1c1',
            borderRadius: '4px',
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: dark ? '#3B5462' : '#a1a1a1',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '10px 24px',
            fontSize: '0.9rem',
            fontWeight: 600,
            transition: 'all 0.2s ease-in-out',
          },
          contained: {
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 18px rgba(8, 145, 178, 0.32)',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
              transform: 'translateY(-1px)',
            },
          },
          sizeSmall: {
            padding: '6px 16px',
            fontSize: '0.8rem',
          },
          sizeLarge: {
            padding: '14px 32px',
            fontSize: '1rem',
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${dark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.06)'}`,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: `0 8px 24px ${sh(0.08)}`,
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'medium',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: `0 2px 8px ${sh(0.06)}`,
              },
              '&.Mui-focused': {
                boxShadow: '0 2px 12px rgba(8, 145, 178, 0.18)',
              },
            },
          },
        },
      },
      /*
       * Every dropdown in the app, styled once here rather than per call site.
       *
       * Doing it in the theme rather than passing MenuProps at ~29 call sites
       * means a select added later inherits it too, instead of quietly
       * shipping the browser default.
       */
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${dark ? 'rgba(255,255,255,0.09)' : '#D9E5EC'}`,
            // Clips the gradient rule to the rounded corners. The height cap
            // is deliberately NOT here: `overflow: hidden` plus a maxHeight on
            // the same element clips the list with no scrollbar, hiding every
            // option past the cut. It goes on the list below, which scrolls.
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background:
                'linear-gradient(90deg, var(--ocean-g1), var(--ocean-g2), var(--ocean-g3))',
              zIndex: 1,
            },
          },
          list: {
            paddingTop: 6,
            paddingBottom: 6,
            // A long list scrolls inside the menu; nothing is unreachable.
            maxHeight: 340,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgb(var(--ocean-accent-rgb) / 0.28)',
              borderRadius: 99,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgb(var(--ocean-accent-rgb) / 0.45)',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            position: 'relative',
            borderRadius: 9,
            marginLeft: 6,
            marginRight: 6,
            transition: 'background-color .2s, padding-left .2s',
            // The gradient rail slides in from the left edge on hover.
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 6,
              bottom: 6,
              width: 3,
              borderRadius: 99,
              background:
                'linear-gradient(180deg, var(--ocean-g1), var(--ocean-g2))',
              transform: 'scaleY(0)',
              transition: 'transform .22s',
            },
            '&:hover': {
              backgroundColor: 'var(--ocean-hover)',
              paddingLeft: 26,
            },
            '&:hover::before, &.Mui-selected::before': { transform: 'scaleY(1)' },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: 'var(--ocean-hover)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            padding: '8px',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            border: 'none',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            backgroundColor: 'var(--ocean-thead)',
            color: subtleText,
            fontSize: '0.8rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
          root: {
            borderBottom: `1px solid ${
              dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
            }`,
            padding: '16px',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            minHeight: 48,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: dark ? '#111E26' : '#ffffff',
            color: dark ? '#E6EFF3' : '#1a2027',
            borderBottom: `1px solid ${
              dark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.06)'
            }`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 8px',
            padding: '10px 16px',
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              backgroundColor: alpha(accent, dark ? 0.16 : 0.08),
              color: accent,
              '&:hover': {
                backgroundColor: alpha(accent, dark ? 0.22 : 0.12),
              },
              '& .MuiListItemIcon-root': {
                color: accent,
              },
            },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            flexShrink: 0,
          },
        },
      },
    },
  });
};

/** The light theme, kept as the default export for plain importers. */
const theme = buildTheme('light');

export default theme;
